import React, { useEffect, useId, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, HelpCircle, Minus, X } from 'lucide-react';
import { useDialogA11y } from '../../landing/lib/useDialogA11y';
import { getListingFit, getProviderFit } from '../../services/interestFitService';
import { parseEngagementRefusal, resolveEngagementGateCopy } from '../../utils/engagementGate';
import {
  OUTCOME_WORDS,
  cancelLabel,
  confirmLabel,
  countDifferences,
  groupRows,
  headerCopy,
  loadingCopy,
  sideLabels,
  subjectLine,
  summaryCopy,
  unavailableCopy,
} from './interestFitCopy';
import './InterestFitConfirmation.css';

const ICONS = {
  match: Check,
  partial: Minus,
  mismatch: X,
  unknown: HelpCircle,
};

function FitRow({ row, audience }) {
  const Icon = ICONS[row.outcome] || HelpCircle;
  const sides = sideLabels(audience);
  const hasSides = row.listing || row.provider;
  return (
    <li
      className={`ifc-row ifc-row--${row.outcome}`}
      data-testid={`fit-row-${row.key}`}
      data-outcome={row.outcome}
    >
      <span className="ifc-row__icon" aria-hidden="true">
        <Icon size={16} />
      </span>
      <div className="ifc-row__text">
        <div className="ifc-row__head">
          <span className="ifc-row__label">{row.label}</span>
          <span className="ifc-row__verdict">{OUTCOME_WORDS[row.outcome] || ''}</span>
        </div>
        <p className="ifc-row__detail">{row.detail}</p>
        {hasSides && (
          <dl className="ifc-row__sides">
            <div>
              <dt>{sides.listing}</dt>
              <dd>{row.listing || 'Not stated'}</dd>
            </div>
            <div>
              <dt>{sides.provider}</dt>
              <dd>{row.provider || 'Not stated'}</dd>
            </div>
          </dl>
        )}
      </div>
    </li>
  );
}

function RowGroup({ id, title, rows, audience, open }) {
  if (!rows.length) return null;
  const heading = `${title} (${rows.length})`;
  if (open === false) {
    return (
      <details className="ifc__group ifc__group--collapsed">
        <summary className="ifc__group-toggle">
          <h3 id={id} className="ifc__group-title">{heading}</h3>
        </summary>
        <ul className="ifc__rows">
          {rows.map((row) => <FitRow key={row.key} row={row} audience={audience} />)}
        </ul>
      </details>
    );
  }
  return (
    <section className="ifc__group" aria-labelledby={id}>
      <h3 id={id} className="ifc__group-title">{heading}</h3>
      <ul className="ifc__rows">
        {rows.map((row) => <FitRow key={row.key} row={row} audience={audience} />)}
      </ul>
    </section>
  );
}

/**
 * One explicit confirmation before any interest is recorded, with the matches
 * and the discrepancies between the listing and the provider in front of the
 * person confirming. Fetches its own report; the caller only says who is
 * asking and about which pair.
 *
 * Three states, and the confirm button is enabled in exactly two of them:
 *   loading       the report is on its way; nothing to confirm yet.
 *   ready         the report is shown; confirming records the acknowledgement.
 *   unavailable   the report could not be fetched; confirming is still allowed
 *                 (a flaky network must not block a real decision) but says so.
 * A fourth, blocked, is a company-only refusal from the engagement gate: the
 * swipe would be refused too, so the dialog offers only Close.
 */
export default function InterestFitConfirmation({
  audience = 'provider',
  listingId,
  providerProfileId,
  subject = null,
  onRefusal,
  onCancel,
  onConfirm,
}) {
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [refusal, setRefusal] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const dialogRef = useDialogA11y({ onClose: onCancel });
  const copy = headerCopy(audience);
  // Unique per mounted dialog: several hosts own a hook instance each.
  const uid = useId();
  const ids = {
    title: `${uid}-title`,
    help: `${uid}-help`,
    summary: `${uid}-summary`,
    differences: `${uid}-differences`,
    matches: `${uid}-matches`,
    unknowns: `${uid}-unknowns`,
  };

  useEffect(() => {
    let active = true;
    setError(null);
    setRefusal(null);
    setReport(null);
    const request =
      audience === 'company'
        ? getProviderFit(providerProfileId, listingId)
        : getListingFit(listingId);
    request
      .then((loaded) => {
        if (!active) return;
        if (!loaded || !Array.isArray(loaded.rows)) {
          setError('load');
          return;
        }
        setReport(loaded);
      })
      .catch((failure) => {
        if (!active) return;
        const gate = audience === 'company' ? parseEngagementRefusal(failure) : null;
        if (gate) {
          setRefusal(resolveEngagementGateCopy(gate) || {
            title: 'This listing cannot be sent to this provider',
            body: failure?.message || '',
          });
          if (typeof onRefusal === 'function') onRefusal(gate);
          return;
        }
        setError('load');
      });
    return () => {
      active = false;
    };
    // onRefusal is a stable callback owned by the host; re-fetching when it
    // changes identity would re-run the gate for nothing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audience, listingId, providerProfileId, attempt]);

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  const groups = useMemo(() => groupRows(report), [report]);
  const differences = countDifferences(report);
  const loading = !report && !error && !refusal;
  const line = subjectLine(report?.listing) || subjectLine(subject);

  return createPortal(
    <div
      className="ifc__backdrop"
      onClick={(event) => {
        event.stopPropagation();
        if (event.target === event.currentTarget) onCancel();
      }}
    >
      <section
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={ids.title}
        aria-describedby={`${ids.help} ${ids.summary}`}
        tabIndex={-1}
        className={`ifc ifc--${audience}`}
        data-testid="interest-fit-confirmation"
        aria-busy={loading ? 'true' : 'false'}
      >
        <header className="ifc__header">
          <div>
            <p className="ifc__eyebrow">{copy.eyebrow}</p>
            <h2 id={ids.title}>{copy.title}</h2>
            <p id={ids.help} className="ifc__subject">{line || copy.eyebrow}</p>
          </div>
          <button
            type="button"
            className="ifc__close"
            onClick={onCancel}
            aria-label="Close without confirming"
          >
            <X size={22} />
          </button>
        </header>

        {/*
          The only scroll container in a scroll-locked page, so it is in the
          Tab order (tabIndex 0): a keyboard user reaches it, and arrow keys
          then scroll the report. role="region" names it for screen readers.
        */}
        <div
          className="ifc__body"
          tabIndex={0}
          role="region"
          aria-label="Fit report"
        >
          {loading && <p role="status" className="ifc__status">{loadingCopy(audience)}</p>}

          {refusal && (
            <div role="alert" className="ifc__alert ifc__alert--blocked">
              <h3>{refusal.title}</h3>
              {refusal.body && <p>{refusal.body}</p>}
            </div>
          )}

          {error && !refusal && (
            <div role="alert" className="ifc__alert">
              <p>{unavailableCopy(audience)}</p>
              <button
                type="button"
                className="ifc__link"
                onClick={() => setAttempt((n) => n + 1)}
              >
                Try again
              </button>
            </div>
          )}

          {report && (
            <>
              <p
                id={ids.summary}
                className="ifc__summary"
                data-testid="fit-summary"
                role="status"
              >
                {summaryCopy(audience, report)}
              </p>
              <RowGroup
                id={ids.differences}
                title="Does not match"
                rows={groups.differences}
                audience={audience}
              />
              <RowGroup
                id={ids.matches}
                title="Matches"
                rows={groups.matches}
                audience={audience}
              />
              <RowGroup
                id={ids.unknowns}
                title="Not compared"
                rows={groups.unknowns}
                audience={audience}
                open={false}
              />
            </>
          )}
        </div>

        <footer className="ifc__footer">
          {refusal ? (
            <button type="button" className="ifc__secondary" onClick={onCancel}>
              Close
            </button>
          ) : (
            <>
              <button type="button" className="ifc__secondary" onClick={onCancel}>
                {cancelLabel(audience)}
              </button>
              <button
                type="button"
                className="ifc__primary"
                disabled={loading}
                onClick={() => {
                  if (!loading) onConfirm();
                }}
              >
                {loading ? 'Comparing...' : confirmLabel(audience, differences)}
              </button>
            </>
          )}
        </footer>
      </section>
    </div>,
    document.body,
  );
}
